import { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard, Users, Building2, Contact, ListChecks, CalendarCheck,
  BarChart3, Bell, LogOut, Menu, X, Ship, ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "employee"] },
  { to: "/customers", label: "Customers", icon: Contact, roles: ["admin", "employee"] },
  { to: "/tasks", label: "Tasks", icon: ListChecks, roles: ["admin", "employee"] },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["admin", "employee"] },
  { to: "/compliance", label: "Follow-up Compliance", icon: CalendarCheck, roles: ["admin"] },
  { to: "/employees", label: "Employees", icon: Users, roles: ["admin"] },
  { to: "/departments", label: "Departments", icon: Building2, roles: ["admin"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        setUnread(data.filter((n) => !n.read).length);
      } catch {}
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const links = NAV.filter((n) => n.roles.includes(user?.role));

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      <div className="px-5 h-16 flex items-center gap-2 border-b border-border">
        <div className="h-9 w-9 bg-primary text-primary-foreground flex items-center justify-center rounded-sm">
          <Ship className="h-5 w-5" strokeWidth={2.2} />
        </div>
        <div className="leading-tight">
          <p className="font-display font-black tracking-tight text-lg">VEAIMPEX</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">VIPL Ops</p>
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.to === "/"}
            onClick={() => setOpen(false)}
            data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-sm transition-colors border border-transparent",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground hover:bg-secondary hover:border-border"
              )
            }
          >
            <l.icon className="h-4.5 w-4.5 h-[18px] w-[18px]" strokeWidth={2} />
            {l.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-border">
        <div className="flex items-center gap-2 px-2 py-2 mb-2">
          <div className="h-9 w-9 bg-secondary flex items-center justify-center rounded-sm font-display font-bold text-sm">
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold truncate">{user?.name}</p>
            <p className="text-[11px] text-muted-foreground flex items-center gap-1">
              {user?.role === "admin" && <ShieldCheck className="h-3 w-3" />}
              {user?.role === "admin" ? "Administrator" : user?.department}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          className="w-full justify-start gap-2 rounded-sm"
          onClick={async () => { await logout(); nav("/login"); }}
          data-testid="logout-button"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 border-r border-border bg-card flex-col fixed inset-y-0">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-card border-r border-border">
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-card flex items-center justify-between px-4 sm:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground hidden sm:block">
              Staff & Customer POC Management
            </p>
          </div>
          <button
            onClick={() => nav("/notifications")}
            className="relative h-9 w-9 flex items-center justify-center border border-border rounded-sm hover:bg-secondary transition-colors"
            data-testid="notifications-bell"
          >
            <Bell className="h-4.5 w-4.5 h-[18px] w-[18px]" />
            {unread > 0 && (
              <span className="absolute -top-1.5 -right-1.5 h-4 min-w-4 px-1 bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center rounded-full">
                {unread}
              </span>
            )}
          </button>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto animate-fade-up">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
